import { Link } from 'react-router'
import { useTranslation } from 'react-i18next'

import { ComplianceBadge } from '../brokers/ComplianceBadge'
import type { BrokerOption, CarrierOption, DocumentReview, ReadDocument } from './api'
import { brokerFoundBy, carrierFoundBy, flagText, flagWhere } from './describe'

/**
 * The platform's look at one rate confirmation (3.1, D4): who its broker, the broker's agent and
 * its carrier are in the book, and every flag in one list — each also sits under its field
 * below. Computed when the record was read, so a broker registered since shows here on the
 * next look. It says; it never chooses: confirming a suggestion is the review's (3.2).
 */
export function ReviewPanel({ review, document }: { review: DocumentReview; document: ReadDocument }) {
  const { t, i18n } = useTranslation()
  const named = review.broker.status === 'Known' ? review.broker.brokers[0] : undefined
  const printedAgent =
    document.broker.agentName.value ?? document.broker.agentEmail.value ?? document.broker.agentPhone.value

  return (
    <section className="rc-review" aria-label={t('rateConfirmations.review.title')}>
      <h3 className="rc-section__title">{t('rateConfirmations.review.title')}</h3>

      <dl className="rc-review__parties">
        <div>
          <dt>{t('rateConfirmations.review.broker')}</dt>
          <dd>
            <BrokerAnswer review={review} />
          </dd>
        </div>
        {named !== undefined && (
          <div>
            <dt>{t('rateConfirmations.review.agent')}</dt>
            <dd>
              {review.agent !== null ? (
                <>
                  <Link to={`/brokers/${review.agent.brokerId}`}>{review.agent.agentName}</Link>{' '}
                  <span className="muted">{brokerFoundBy(review.agent, t)}</span>
                </>
              ) : printedAgent !== null ? (
                t('rateConfirmations.review.agentNotListed', { printed: printedAgent, broker: named.name })
              ) : (
                <span className="muted">{t('rateConfirmations.review.agentNotPrinted')}</span>
              )}
            </dd>
          </div>
        )}
        <div>
          <dt>{t('rateConfirmations.review.carrier')}</dt>
          <dd>
            <CarrierAnswer review={review} />
          </dd>
        </div>
      </dl>

      {review.flags.length === 0 ? (
        <p className="rc-review__clear">{t('rateConfirmations.review.nothingToCheck')}</p>
      ) : (
        <div className="alert alert--warning rc-review__flags">
          <p>{t('rateConfirmations.review.toCheck', { count: review.flags.length })}</p>
          <ul>
            {review.flags.map((flag, index) => {
              const where = flagWhere(flag, t)
              return (
                <li key={`${flag.code}-${index}`}>
                  {where !== null && <strong>{where}: </strong>}
                  {flagText(flag, t, i18n.language, document.money.currency.value)}
                </li>
              )
            })}
          </ul>
        </div>
      )}
    </section>
  )
}

function BrokerAnswer({ review }: { review: DocumentReview }) {
  const { t } = useTranslation()
  const { status, brokers, stoodDown } = review.broker

  return (
    <>
      {status === 'Known' && brokers[0] !== undefined && <BrokerLine option={brokers[0]} />}
      {status !== 'Known' && <p className="rc-review__line">{t(`rateConfirmations.review.brokerStatus.${status}`)}</p>}
      {status !== 'Known' && brokers.length > 0 && (
        <ul className="rc-review__options">
          {brokers.map((option) => (
            <li key={option.brokerId}>
              <BrokerLine option={option} />
            </li>
          ))}
        </ul>
      )}
      <Inactive names={stoodDown.map((option) => ({ id: option.brokerId, name: option.name, to: `/brokers/${option.brokerId}` }))} />
    </>
  )
}

function BrokerLine({ option }: { option: BrokerOption }) {
  const { t } = useTranslation()

  return (
    <span className="rc-review__line">
      <Link to={`/brokers/${option.brokerId}`}>{option.name}</Link> <ComplianceBadge state={option.complianceState} />{' '}
      <span className="muted">{brokerFoundBy(option, t)}</span>
    </span>
  )
}

function CarrierAnswer({ review }: { review: DocumentReview }) {
  const { t } = useTranslation()
  const { status, carriers, stoodDown } = review.carrier

  return (
    <>
      {status === 'Known' && carriers[0] !== undefined && <CarrierLine option={carriers[0]} />}
      {status !== 'Known' && <p className="rc-review__line">{t(`rateConfirmations.review.carrierStatus.${status}`)}</p>}
      {status !== 'Known' && carriers.length > 0 && (
        <ul className="rc-review__options">
          {carriers.map((option) => (
            <li key={option.carrierId}>
              <CarrierLine option={option} />
            </li>
          ))}
        </ul>
      )}
      <Inactive
        names={stoodDown.map((option) => ({ id: option.carrierId, name: option.name, to: `/carriers/${option.carrierId}` }))}
      />
    </>
  )
}

function CarrierLine({ option }: { option: CarrierOption }) {
  const { t } = useTranslation()

  return (
    <span className="rc-review__line">
      <Link to={`/carriers/${option.carrierId}`}>{option.name}</Link> <ComplianceBadge state={option.complianceState} />{' '}
      <span className="muted">{carrierFoundBy(option, t)}</span>
    </span>
  )
}

/** Records the document points at that are inactive: named, never offered (TRKC-110's word). */
function Inactive({ names }: { names: readonly { id: string; name: string; to: string }[] }) {
  const { t } = useTranslation()

  if (names.length === 0) {
    return null
  }

  return (
    <p className="rc-review__line">
      {t('rateConfirmations.review.inactive')}{' '}
      {names.map((record, index) => (
        <span key={record.id}>
          {index > 0 && ', '}
          <Link to={record.to}>{record.name}</Link>
        </span>
      ))}
    </p>
  )
}
